import React from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineClipboardList,
  HiOutlineLink,
  HiOutlineSparkles,
  HiOutlineCreditCard,
  HiOutlineMail,
  HiOutlinePencilAlt,
  HiOutlineGift,
  HiOutlineSearch,
} from "react-icons/hi";
import { HiOutlineUserPlus } from "react-icons/hi2";
import GettingStarted from "../../../molecules/GettingStarted";

const SignupFormsGettingStarted: React.FC = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: <HiOutlineClipboardList size={22} />,
      title: t("signupForms.gettingStarted.steps.step1.title") as string,
      body: t("signupForms.gettingStarted.steps.step1.body") as string,
      className: "sm:col-span-2 lg:col-span-1",
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="flex items-center gap-2 mb-2 min-w-0">
            <span className="text-xs uppercase tracking-wide text-gray-400 font-medium flex-shrink-0">
              {t("signupForms.gettingStarted.steps.step1.mockupLabel")}
            </span>
            <span className="flex-1 border-b border-dashed border-gray-300 min-w-[20px]" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
              <HiOutlineCreditCard size={12} />
              {t("signupForms.gettingStarted.steps.step1.chipPayment")}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
              <HiOutlinePencilAlt size={12} />
              {t("signupForms.gettingStarted.steps.step1.chipEsign")}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-50 text-green-700 text-xs font-medium">
              <HiOutlineMail size={12} />
              {t("signupForms.gettingStarted.steps.step1.chipFollowUp")}
            </span>
          </div>
        </div>
      ),
      tip: t("signupForms.gettingStarted.steps.step1.tip") as string,
    },
    {
      icon: <HiOutlineLink size={22} />,
      title: t("signupForms.gettingStarted.steps.step2.title") as string,
      body: t("signupForms.gettingStarted.steps.step2.body") as string,
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0">
              <HiOutlineLink size={15} />
            </div>
            <div className="min-w-0 flex-1 px-2 py-1 rounded bg-gray-50 border border-gray-200 text-xs text-gray-500 font-mono truncate">
              {t("signupForms.gettingStarted.steps.step2.mockupLink")}
            </div>
          </div>
          <div className="mt-2.5 px-2 py-1 rounded bg-blue-50 border border-blue-100 text-xs text-blue-700 font-medium inline-flex items-start gap-1 max-w-full">
            <HiOutlineSparkles size={13} className="flex-shrink-0 mt-px" />
            <span className="break-words min-w-0">
              {t("signupForms.gettingStarted.steps.step2.mockupChannels")}
            </span>
          </div>
        </div>
      ),
    },
    {
      icon: <HiOutlineUserPlus size={22} />,
      title: t("signupForms.gettingStarted.steps.step3.title") as string,
      body: t("signupForms.gettingStarted.steps.step3.body") as string,
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineUserPlus size={15} className="text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="h-1.5 bg-gray-200 rounded w-16 sm:w-20 mb-1" />
                <div className="h-1 bg-gray-100 rounded w-10 sm:w-12" />
              </div>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-semibold flex-shrink-0">
              <HiOutlineSparkles size={12} />
              {t("signupForms.gettingStarted.steps.step3.mockupBadge")}
            </span>
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 text-gray-600 text-xs font-medium">
              <HiOutlineGift size={12} className="text-blue-500" />
              {t("signupForms.gettingStarted.steps.step3.unlockLoyalty")}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 text-gray-600 text-xs font-medium">
              <HiOutlineMail size={12} className="text-blue-500" />
              {t("signupForms.gettingStarted.steps.step3.unlockCampaigns")}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 text-gray-600 text-xs font-medium">
              <HiOutlineSearch size={12} className="text-blue-500" />
              {t("signupForms.gettingStarted.steps.step3.unlockSearch")}
            </span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <GettingStarted
      icon={<HiOutlineUserPlus className="w-6 h-6 sm:w-7 sm:h-7" />}
      title={t("signupForms.gettingStarted.title") as string}
      subtitle={t("signupForms.gettingStarted.subtitle") as string}
      steps={steps}
      banner={
        <div className="mt-6 sm:mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs uppercase tracking-wider text-gray-400 text-center mb-3 font-medium">
            {t("signupForms.gettingStarted.trustTitle")}
          </p>
          <div className="flex flex-col xs:flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <HiOutlineLink
                size={16}
                className="text-blue-600 flex-shrink-0"
              />
              <span className="text-center">
                {t("signupForms.gettingStarted.trust.ownLink")}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <HiOutlineClipboardList
                size={16}
                className="text-blue-600 flex-shrink-0"
              />
              <span className="text-center">
                {t("signupForms.gettingStarted.trust.customQuestions")}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <HiOutlineSparkles
                size={16}
                className="text-green-600 flex-shrink-0"
              />
              <span className="text-center">
                {t("signupForms.gettingStarted.trust.multipleForms")}
              </span>
            </span>
          </div>
        </div>
      }
    />
  );
};

export default SignupFormsGettingStarted;
